import { useQuery } from "react-query";
import Select from "react-select";
import {
  useReducer,
  useState,
  useMemo,
  useCallback,
  memo,
  useRef,
} from "react";
import { filterTableReducer, filterTableActions } from "./reducers";
import { FiChevronLeft, FiChevronRight, FiLoader } from "react-icons/fi";
import { IoMdRefresh } from "react-icons/io";
import _ from "lodash";
import { useEffect } from "react";
import debounce from "lodash/debounce";

const parseFilters = (filters = {}) => {
  return _.pickBy(filters, (value, key) => value !== "");
};

const TablePagination = ({
  currentPage,
  lastPage,
  setPage,
  nextPage,
  prevPage,
}) => (
  <div className="ml-1 btn-group" role="group" aria-label="Basic example">
    <button
      type="button"
      className="btn btn-secondary btn-sm text-white"
      onClick={prevPage}
      disabled={currentPage <= 1}
    >
      <FiChevronLeft size={15} />
    </button>
    {currentPage !== 1 && (
      <>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setPage(1)}
        >
          <small>{1}</small>
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm text-white"
          disabled={true}
        >
          <small>...</small>
        </button>
      </>
    )}
    <button type="button" className="btn btn-info btn-sm">
      <small>{currentPage}</small>
    </button>
    {currentPage !== lastPage && lastPage !== 0 && (
      <>
        <button
          type="button"
          className="btn btn-secondary btn-sm text-white"
          disabled={true}
        >
          <small>...</small>
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm text-white"
          onClick={() => setPage(lastPage)}
        >
          <small>{lastPage}</small>
        </button>
      </>
    )}
    <button
      type="button"
      className="btn btn-secondary btn-sm text-white"
      onClick={nextPage}
      disabled={currentPage >= lastPage}
    >
      <FiChevronRight size={15} />
    </button>
  </div>
);

export const FilterableTable = memo(
  ({
    columns = {},
    filters = [],
    small = false,
    title,
    className,
    maxHeight,
    refetchCheck,
    query,
    queryKey,
    pageSize = 10,
    refetchOnWindowFocus = false,
    enabled = true,
    action,
  }) => {
    const [filterToExecute, setFilterToExecute] = useState(undefined);

    const queryToExecute = query(filterToExecute);

    const initialFilters = useMemo(() => {
      const _initialFilters = Object.fromEntries([
        ...filters.map(({ filterName, value }) => [filterName, value || ""]),
        ["page", 1],
        ["size", pageSize],
      ]);
      setFilterToExecute(_initialFilters);
      return _initialFilters;
    }, [filters]);

    const [filterState, filterDispatch] = useReducer(filterTableReducer, {
      initialFilters,
      filters: initialFilters,
    });

    const { data, isLoading, isFetching, refetch } = useQuery(
      [queryKey, filterToExecute],
      queryToExecute,
      {
        refetchOnWindowFocus,
        enabled: filterToExecute !== undefined && enabled,
      },
    );

    const [dynamicPageSize, setDynamicPageSize] = useState(pageSize);

    const tableData = useMemo(() => {
      if (!data) return [];
      return data.items?.map((item) => ({ ...item, action }));
    }, [data, action]);

    const handleChange = useCallback(
      (filterName, filterValue) => {
        filterDispatch({
          type: filterTableActions.STATE_CHANGE,
          payload: {
            filterName,
            filterValue,
          },
        });
        setDynamicPageSize(pageSize);
      },
      [pageSize],
    );

    const lastPage = useMemo(() => {
      if (!data) return "...";
      return Math.ceil(data?.total / data?.size);
    }, [data]);

    const onSearch = useCallback(() => {
      handleChange("page", 1);
      setFilterToExecute(
        parseFilters({ ...filterState.filters, page: 1, size: dynamicPageSize }),
      );
      handleChange("size", dynamicPageSize);
    }, [filterState?.filters, dynamicPageSize]);

    const resetAndSearch = useCallback(() => {
      setFilterToExecute(parseFilters(initialFilters));
      filterDispatch({ type: filterTableActions.RESET_FILTER });
    }, [filterState]);

    const prevPage = useCallback(() => {
      if (filterState.filters.page <= 1) return;
      setFilterToExecute(
        parseFilters({
          ...filterState.filters,
          page: filterState.filters.page - 1,
        }),
      );
      filterDispatch({ type: filterTableActions.PREVIOUS_PAGE });
    }, [filterState.filters]);

    const nextPage = useCallback(() => {
      if (filterState.filters.page >= lastPage) return;
      setFilterToExecute(
        parseFilters({
          ...filterState.filters,
          page: filterState.filters.page + 1,
        }),
      );
      filterDispatch({ type: filterTableActions.NEXT_PAGE });
    }, [lastPage, filterState.filters]);

    const setPage = useCallback(
      (pageToSet) => {
        setFilterToExecute(
          parseFilters({ ...filterState.filters, page: pageToSet }),
        );
        filterDispatch({
          type: filterTableActions.SET_PAGE,
          payload: pageToSet,
        });
      },
      [filterState.filters],
    );

    useEffect(() => {
      refetch();
    }, [refetchCheck]);

    return (
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          ...(maxHeight && { maxHeight }),
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            border: "2px solid #27293D",
            backgroundColor: "#1E1E27",
            borderRadius: 15,
          }}
          className={`${className} compact`}
        >
          <div className="d-flex mx-1 p-2 justify-content-between">
            {title && (
              <div className="text-nowrap text-info font-weight-bold display-5">
                {title}
              </div>
            )}
            <a
              href="#"
              onClick={() => {
                !isLoading && !isFetching && refetch();
              }}
            >
              <IoMdRefresh
                size={20}
                className="text-white"
                style={{ cursor: "pointer" }}
              />
            </a>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSearch();
            }}
          >
            {filters?.length ? (
              <>
                <div className="d-flex flex-wrap m-2" style={{ columnGap: 5 }}>
                  {filters.map(
                    (
                      {
                        filterName,
                        placeholder,
                        type = "text",
                        label,
                        options,
                        isLoading: optionsLoading,
                        isDisabled,
                        noOptionsMessage,
                        value,
                        id,
                      },
                      index,
                    ) => {
                      switch (true) {
                        case type === "checkbox":
                          return (
                            <label
                              key={id}
                              className="form-check-label px-5 mt-1"
                            >
                              <input
                                className="form-check-input"
                                type="checkbox"
                                onChange={(e) =>
                                  handleChange(filterName, e.target.checked)
                                }
                              />
                              <span className="form-check-sign"></span>
                              <small className="text-secondary">{label}</small>
                            </label>
                          );
                        case type === "searchable-select":
                          return (
                            <div
                              key={filterName}
                              className="form-group"
                              style={{ width: 250 }}
                            >
                              <Select
                                inputId={"table-filter-" + filterName}
                                aria-label={label || placeholder}
                                className="react-select info"
                                classNamePrefix="react-select"
                                placeholder={placeholder}
                                options={options}
                                value={
                                  options?.find(
                                    (option) =>
                                      option.value === filterState.filters[filterName],
                                  ) || null
                                }
                                onChange={(option) =>
                                  handleChange(filterName, option?.value ?? "")
                                }
                                isSearchable
                                isClearable
                                isLoading={optionsLoading}
                                isDisabled={isDisabled}
                                noOptionsMessage={noOptionsMessage}
                              />
                            </div>
                          );
                        case type === "select":
                          return (
                            <div key={id} className="form-group d-flex mt-1">
                              <label
                                htmlFor={filterName}
                                className="ml-3 mr-2 text-nowrap text-white"
                              >
                                <small>{label}</small>
                              </label>
                              <select
                                id={filterName}
                                className="form-control-sm form-control py-0"
                                onChange={(event) =>
                                  handleChange(filterName, event.target.value)
                                }
                              >
                                {options.map(({ value, label }) => (
                                  <option value={value} key={value}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        case type === "hidden":
                          return null;
                        default:
                          return (
                            <div
                              key={id}
                              className={`${label ? "input-group" : "form-group"}`}
                              style={{ width: 250 }}
                              key={index}
                            >
                              {label && (
                                <label
                                  htmlFor={
                                    "table-filter-index" + index + filterName
                                  }
                                  className="input-group-prepend mb-0"
                                  style={{
                                    backgroundColor: "#1D253B",
                                    height: 35,
                                  }}
                                >
                                  <span className={`input-group-text px-2`}>
                                    <small>{label}</small>
                                  </span>
                                </label>
                              )}
                              <input
                                className="form-control form-control-sm px-2"
                                id={"table-filter-index" + index + filterName}
                                style={{ height: 35 }}
                                placeholder={placeholder}
                                value={filterState.filters[filterName]}
                                type={type}
                                onChange={(e) =>
                                  handleChange(filterName, e.target.value)
                                }
                              />
                            </div>
                          );
                      }
                    },
                  )}
                  <div className="input-group" style={{ width: 150 }}>
                    <div
                      className="input-group-prepend"
                      style={{ backgroundColor: "#1D253B", height: 35 }}
                    >
                      <span className={`input-group-text px-2`}>
                        <small>Page Size</small>
                      </span>
                    </div>
                    <input
                      className="form-control form-control-sm px-2"
                      style={{ height: 35 }}
                      value={dynamicPageSize}
                      type="number"
                      onChange={(e) => setDynamicPageSize(e.target.value)}
                    />
                  </div>

                  <input
                    type="submit"
                    onClick={(e) => {
                      e.preventDefault();
                      onSearch();
                    }}
                    hidden
                  />
                </div>
              </>
            ) : null}
          </form>
          <div className="d-flex mx-2 mb-2">
            {filters.length && (
              <>
                <button
                  className="btn btn-sm btn-info"
                  onClick={onSearch}
                  size="sm"
                  disabled={isFetching}
                >
                  <small>Search</small>
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={resetAndSearch}
                  size="sm"
                  disabled={isFetching}
                >
                  <small>Reset</small>
                </button>
              </>
            )}
          </div>
          <small>
            <TablePagination
              currentPage={filterState.filters.page}
              lastPage={lastPage}
              nextPage={nextPage}
              prevPage={prevPage}
              setPage={setPage}
            />
            <table
              className="table-sm table-responsive"
              style={{ minWidth: "100%", width: "100%" }}
            >
              <thead>
                <tr>
                  {columns.map((column, index) => (
                    <th
                      scope="col"
                      key={column.id + index}
                      className={`text-nowrap text-info px-2 text-nowrap text-${column.alignment}`}
                      style={{
                        border: "2px solid #27293D",
                      }}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Boolean(columns.length) && !isFetching && tableData?.length ? (
                  tableData?.map((row, i) => (
                    <tr className={`text-white`} key={row?.id ?? i}>
                      {columns.map((column, j) => (
                        <td
                          key={column.id + j + i}
                          className={`text-nowrap px-2 text-${column.alignment}`}
                          style={{
                            border: "2px solid #27293D",
                          }}
                        >
                          {column?.accessor ? (
                            column.accessor instanceof Function ? (
                              column.accessor(row) === undefined ? (
                                <span className="text-muted">None</span>
                              ) : (
                                column.accessor(row)
                              )
                            ) : (
                              row[column?.accessor]
                            )
                          ) : (
                            column.action(row) || (
                              <span className="text-muted">No data</span>
                            )
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="text-muted">
                    <td
                      colSpan={columns.length + 1}
                      className="ml-3 text-center"
                    >
                      {isFetching ? (
                        <FiLoader className="animate-spin" size={24} />
                      ) : (
                        "No data yet"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </small>
          <TablePagination
            currentPage={filterState.filters.page}
            lastPage={lastPage}
            nextPage={nextPage}
            prevPage={prevPage}
            setPage={setPage}
          />
        </div>
      </div>
    );
  },
);

FilterableTable.displayName = "FilterableTable";
